import React, { useRef, useEffect } from "react";

/**
 * SignatureCanvas
 * Un composant canvas simple pour capturer une signature dessinée au doigt/souris.
 * Props :
 * - value: (optionnel) imageData (base64) à afficher
 * - onChange: callback(imageData) appelé à chaque modification
 * - width, height: dimensions du canvas
 * - disabled: désactive le dessin
 */
export default function SignatureCanvas({
  value,
  onChange,
  width = 320,
  height = 120,
  disabled = false,
}) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (value && canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      const img = new window.Image();
      img.onload = () => {
        ctx.clearRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);
      };
      img.src = value;
    }
  }, [value, width, height]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    if (e.touches) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const start = (e) => {
    if (disabled) return;
    drawing.current = true;
    last.current = getPos(e);
  };
  const move = (e) => {
    if (!drawing.current || disabled) return;
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#222";
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    const pos = getPos(e);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    last.current = pos;
    if (onChange) {
      onChange(canvasRef.current.toDataURL());
    }
  };
  const end = () => {
    drawing.current = false;
  };
  const clear = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, width, height);
    if (onChange) onChange(null);
  };

  return (
    <div className="flex flex-col items-center">
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        className={`border rounded bg-white ${disabled ? "opacity-60" : "cursor-crosshair"}`}
        style={{ touchAction: "none" }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        disabled={disabled}
      />
      {!disabled && (
        <button
          type="button"
          className="mt-2 text-xs text-blue-600 underline"
          onClick={clear}
        >
          Effacer la signature
        </button>
      )}
    </div>
  );
}
